/* See UserWizardProfileStep: labels are associated explicitly via htmlFor + the
   control's DOM id rather than by wrapping the control. */
/* eslint jsx-a11y/label-has-associated-control: ["error", { "assert": "htmlFor" }] */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { z } from 'zod';

import ConfirmationModal from 'src/components/modals/ConfirmationModal/ConfirmationModal';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import ContainerBody from 'src/components/ui/ContainerOverlay/ContainerBody/ContainerBody';
import { ControlledInput } from 'src/components/ui/Input/Input';
import OverlayHead from 'src/components/ui/Overlay/OverlayHead';
import TabBar from 'src/components/layouts/TabBar/TabBar';
import { OverlayActions, useOverlay } from 'src/providers/OverlayProvider';
import { extractBackendErrorMessage } from 'src/pages/Register/errorUtils';
import { useUserMutations, useUserQuery } from 'src/services/user';
import { formatDate, getLangCode, transformPermissionsToRoles } from 'src/utils/general';
import logger from 'src/utils/logger';
import { UserGeneralForm, UserGeneralFormRef } from './UserGeneralForm';
import { UserPreferencesForm, UserPreferencesFormRef } from './UserPreferencesForm';
import { useOverlayCloseGuard } from './useOverlayCloseGuard';

const userSchema = z.object({
  name: z.string().trim().min(1, 'Name is required'),
  email: z.string().trim().min(1, 'Email is required').email('Invalid email address'),
});

type UserFormData = z.infer<typeof userSchema>;

type Props = {
  initialMode: 'edit' | 'view';
};

const NAME_INPUT_ID = 'user-detail-name';
const EMAIL_INPUT_ID = 'user-detail-email';

/**
 * Detail view of an existing user, with an inline edit mode. Kept separate from
 * the create wizard on purpose: there is no password, no second step and no
 * "add user" action here, so squeezing both into one wizard frame would mean a
 * component whose two halves share nothing but the drawer.
 */
const UserDetailOverlay: React.FC<Props> = ({ initialMode }) => {
  const { t } = useTranslation();
  const { onClose } = useOverlay();
  const { updateMutation } = useUserMutations();
  const [searchParams] = useSearchParams();
  const view = searchParams.get('view');

  const [mode, setMode] = useState<'edit' | 'view'>(initialMode);
  const [activeTab, setActiveTab] = useState(0);
  const [isCloseConfirmationOpen, setIsCloseConfirmationOpen] = useState(false);
  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] = useState(false);

  const generalFormRef = useRef<UserGeneralFormRef>(null);
  const preferencesFormRef = useRef<UserPreferencesFormRef>(null);

  const { id: idString } = useParams();
  // `??` only catches null/undefined, so a route like /settings/edit/abc would
  // otherwise fire a real `GET /users/user/NaN`.
  const parsedId = idString ? Number.parseInt(idString, 10) : Number.NaN;
  const hasValidId = !Number.isNaN(parsedId);
  const id = hasValidId ? parsedId : undefined;
  const initialUser = useUserQuery(hasValidId ? parsedId : 0, { enabled: hasValidId });

  const { control, handleSubmit, reset } = useForm<UserFormData>({
    resolver: zodResolver(userSchema),
    defaultValues: { name: '', email: '' },
  });

  const isReadOnly = mode === 'view';
  const isAnyModalOpen = isCloseConfirmationOpen || isCancelConfirmationOpen;

  const handleCloseAttempt = () => {
    if (isReadOnly) {
      onClose();
      return;
    }
    setIsCloseConfirmationOpen(true);
  };

  const containerRef = useOverlayCloseGuard(handleCloseAttempt, !isAnyModalOpen);

  const handleConfirmClose = (confirmed: boolean) => {
    setIsCloseConfirmationOpen(false);
    if (confirmed) onClose();
  };

  const handleConfirmCancel = (confirmed: boolean) => {
    setIsCancelConfirmationOpen(false);
    if (confirmed) setMode('view');
  };

  const submitForm = handleSubmit(async data => {
    if (!id) {
      logger.error('User id is undefined. Cannot update user.');
      return;
    }

    const generalData = await generalFormRef.current?.submit?.();
    if (generalData == null) return;

    const preferencesData = preferencesFormRef.current?.getCurrentValues?.();

    try {
      await updateMutation.mutateAsync({
        params: { path: { user_id: id } },
        body: {
          email: data.email.trim(),
          lang: getLangCode(preferencesData?.lang),
          login: null,
          name: data.name.trim(),
          notification_daily_updates: preferencesData?.notifications?.dailyUpdates || false,
          notification_email: preferencesData?.notifications?.email || false,
          notification_sms: preferencesData?.notifications?.sms || false,
          roles: transformPermissionsToRoles(t, generalData.permissions),
          is_in_dark_mode: false,
        },
      });
      toast.success(t('success.user_updated_successfully'));
      // Only leave once the change actually landed — a failed save must not
      // close the overlay and throw the input away.
      onClose();
    } catch (error) {
      logger.error('Failed to update user', error);
      toast.error(extractBackendErrorMessage(error) || t('errors.error_updating_user'));
    }
  });

  const overlayActions = useMemo(() => {
    if (isReadOnly) {
      return (
        <Button
          key="edit"
          title={t('general.edit')}
          tone={ButtonToneProps.PRIMARY_BLUE}
          onClick={() => setMode('edit')}
        />
      );
    }
    return (
      <Button
        key="cancel"
        title={t('general.cancel')}
        tone={ButtonToneProps.SECONDARY}
        onClick={() => setIsCancelConfirmationOpen(true)}
      />
    );
  }, [isReadOnly, t]);

  useEffect(() => {
    if (initialUser.data) {
      reset({ name: initialUser.data.name || '', email: initialUser.data.email || '' });
    }
  }, [initialUser.data]);

  useEffect(() => {
    setMode(id && !view ? 'edit' : initialMode);
  }, [view, id, initialMode]);

  // No `hasError` on the general tab: its schema is all booleans that can only
  // ever validate, so the indicator could never light up.
  const tabs = [
    {
      label: t('general.general'),
      onClick: () => setActiveTab(0),
    },
    {
      label: t('general.preferences'),
      onClick: () => setActiveTab(1),
    },
  ];

  return (
    <>
      <OverlayActions>{overlayActions}</OverlayActions>
      {/* The head has to sit inside the guarded container, otherwise its own
          buttons (close, edit, cancel) would register as clicks "outside". */}
      <div
        ref={containerRef}
        data-overlay-handles-escape="true"
        data-overlay-handles-outside="true"
        className="flex min-h-0 flex-1 flex-col">
        <OverlayHead title={initialUser.data?.name || t('general.view_user')} onCloseOverlay={handleCloseAttempt} />
        <ContainerBody className="flex min-h-0 flex-1 flex-col">
          <div className="flex w-full flex-col items-start gap-12">
            {initialUser.data && (
              <div className="mb-20 flex w-full flex-col gap-12">
                <span className="typo-title-24 text-full">{initialUser.data.name}</span>
                {(initialUser.data as any)?.login_date && (
                  <p className="typo-text-14 text-faded">
                    {t('general.last_login')} {formatDate((initialUser.data as any).login_date)}
                  </p>
                )}
              </div>
            )}

            <div className="flex w-full flex-col gap-12">
              <div className="grid w-full grid-cols-3 items-center gap-10">
                <label htmlFor={NAME_INPUT_ID} className="typo-text-14 text-faded">
                  {t('general.name')}
                </label>
                <ControlledInput
                  id={NAME_INPUT_ID}
                  control={control}
                  name="name"
                  placeholder=""
                  className="col-span-2"
                  disabled={isReadOnly}
                />
              </div>
              <div className="grid w-full grid-cols-3 items-center gap-10">
                <label htmlFor={EMAIL_INPUT_ID} className="typo-text-14 text-faded">
                  {t('general.email')}
                </label>
                <ControlledInput
                  id={EMAIL_INPUT_ID}
                  control={control}
                  name="email"
                  placeholder={t('general.user_wizard.email_placeholder')}
                  className="col-span-2"
                  disabled={isReadOnly}
                />
              </div>
            </div>

            <TabBar className="my-32 w-full" tabs={tabs} defaultIndex={0} />
            <div className={activeTab === 0 ? 'flex w-full' : 'hidden'}>
              <UserGeneralForm ref={generalFormRef} readOnly={isReadOnly} initialData={initialUser.data} />
            </div>
            <div className={activeTab === 1 ? 'flex w-full' : 'hidden'}>
              <UserPreferencesForm ref={preferencesFormRef} readOnly={isReadOnly} initialData={initialUser.data} />
            </div>
          </div>

          {!isReadOnly && (
            <div className="border-subtle-top mt-32 flex w-full items-center justify-end gap-12 py-16">
              <Button
                title={t('general.save')}
                tone={ButtonToneProps.PRIMARY_BLUE}
                onClick={submitForm}
                disabled={updateMutation.isPending}
              />
            </div>
          )}
        </ContainerBody>
      </div>

      <ConfirmationModal
        isOpen={isCloseConfirmationOpen}
        title={t('general.close_overlay')}
        description={t('general.close_overlay_confirmation')}
        onClose={handleConfirmClose}
      />

      <ConfirmationModal
        isOpen={isCancelConfirmationOpen}
        title={t('general.cancel')}
        description={t('general.close_overlay_confirmation')}
        onClose={handleConfirmCancel}
      />
    </>
  );
};

export default UserDetailOverlay;
